import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import * as Handlebars from 'handlebars/dist/cjs/handlebars';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Widget, Dashboard, TimeSeriesDetailRequest } from 'src/api/models';
import { WidgetTextTemplate } from 'src/api/models/widget-text-template';
import { IWidgetEditor, WidgetType } from 'src/app/models/dashboard';
import { DataService } from 'src/api/services/data.service';
import { WidgetTextTemplateService } from 'src/api/services/widget-text-template.service';
import { ToastrService } from 'ngx-toastr';
import { ApplicationConfigService } from 'src/service/application-config.service';

declare const monaco: any;


@Component({
  selector: 'app-free-text-widget-editor',
  templateUrl: './multi-text-widget-editor.component.html',
  styleUrls: ['./multi-text-widget-editor.component.css']
})
export class MultiTextWidgetEditorComponent implements OnInit, OnDestroy, IWidgetEditor {
  @ViewChild('editorTabs', { static: false }) editorTabs: TabsetComponent;

  onHtmlEditorInit(editor: any): void {
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
      () => this.formatHtml()
    );
  }

  public widgetType: WidgetType;
  public widget: Widget;
  public dashboard: Dashboard;
  public siteUri: string;
  public w: any;
  selected: any;

  public previewHtml: string = '';
  public previewData: any = null;
  public isLoadingPreview: boolean = false;
  public templatesLoading: boolean = false;
  public templateTypeFilter: number = 1;
  public selectedTemplate: WidgetTextTemplate | null = null;
  public templates: WidgetTextTemplate[] = [];
  public previewImageUrls = new Map<number, SafeUrl>();
  private previewObjectUrls = new Map<number, string>();

  get filteredTemplates(): WidgetTextTemplate[] {
    return this.templates.filter(t => t.type === this.templateTypeFilter);
  }

  get hasAnySeriesUri(): boolean {
    return (this.w?.timeSeriesDetailList || []).some((ts: any) => ts.uri?.trim());
  }
  //public example: string = "<div class=\"card\">\n  <div class=\"card-body\">\n      <div class=\"h3\">{{uniquename.data.db_decoded.deviceType}}</div>\n        <ul>\n{{#each uniquename.data.calculations}}\n         <li>{{this.Duration}}</li>\n{{/each}}\n      </ul>\n      <div>Wind Speed:{{uniquename.data.db_decoded.windSpeed}}</div>\n      <div>Meta Data:{{uniquename.metaData.siteType}}</div>\n      <div>Latitude:{{uniquename.latitude}}</div>\n      <div>Longitude:{{uniquename.longitude}}</div>\n  </div>\n</div>";
  public example: string ="<div class=\"card\">\n\t<div class=\"card-body\">\n\t\t<table class=\"table\">\n\t\t\t<tr>\n\t\t\t\t<th></th>\n\t\t\t\t<th>Coup</th>\n\t\t\t\t<th>Open</th>\n\t\t\t\t<th>Shade</th>\n\t\t\t</tr>\n\t\t\t{{#filterByMetaTag list 'shade' 'false'}}\n\t\t\t\t<tr>\n\t\t\t\t\t<td>{{@index}}</td>\n\t\t\t\t\t<td>{{this.metaData.site}}</td>\n\t\t\t\t\t{{#if this.data}}\n\t\t\t\t\t\t{{#ifCond this.data '<' 9}}\n\t\t\t\t\t\t\t<td class=\"bg-danger\">\n\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t{{#ifCond this.data '>=' 9}}\n\t\t\t\t\t\t\t{{#ifCond this.data '<' 14}}\n\t\t\t\t\t\t\t\t<td class=\"bg-success\">\n\t\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t{{#ifCond this.data '>=' 14}}\n\t\t\t\t\t\t\t{{#ifCond this.data '<' 16}}\n\t\t\t\t\t\t\t\t<td class=\"bg-danger\">\n\t\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t{{#ifCond data '>=' 16}}\n\t\t\t\t\t\t\t<td class=\"bg-danger\">\n\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t{{else}}\n\t\t\t\t\t\t<td class=\"bg-secondary\">\n\t\t\t\t\t\t\t--\n\t\t\t\t\t\t</td>\n\t\t\t\t\t{{/if}}\n\t\t\t\t\t{{#filterByMetaTag ../list 'shade' 'true'}}\n\t\t\t\t\t\t{{#findByMetaTag this 'site' ../this.metaData.site}}\n\t\t\t\t\t\t\t{{#if this.data}}\n\t\t\t\t\t\t\t\t{{#ifCond this.data '<' 17}}\n\t\t\t\t\t\t\t\t\t<td class=\"bg-danger\">\n\t\t\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t\t\t{{#ifCond this.data '>=' 17}}\n\t\t\t\t\t\t\t\t\t<td class=\"bg-success\">\n\t\t\t\t\t\t\t\t\t\t{{this.data}}%\n\t\t\t\t\t\t\t\t\t\t<br/><div class=\"small\">{{relativeDate this.timeStamp}}</div>\n\t\t\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t\t\t{{/ifCond}}\n\t\t\t\t\t\t\t{{else}}\n\t\t\t\t\t\t\t\t<td class=\"bg-secondary\">\n\t\t\t\t\t\t\t\t\t--\n\t\t\t\t\t\t\t\t\t<br/><div class=\"small\">--</div>\n\t\t\t\t\t\t\t\t</td>\n\t\t\t\t\t\t\t{{/if}}\n\t\t\t\t\t\t{{/findByMetaTag}}\n\t\t\t\t\t{{/filterByMetaTag}}\n\t\t\t\t</tr>\n\t\t\t{{/filterByMetaTag}}\n\t\t</table>\n\t</div>\n</div>";
  constructor(
    private dataService: DataService,
    private toastr: ToastrService,
    private app: ApplicationConfigService,
    private templateService: WidgetTextTemplateService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.w = this.widget;
    if (this.w.bandDetailList == null) this.w.bandDetailList = [];
    if (this.w.lineDetailList == null) this.w.lineDetailList = [];
    if (this.w.dataSourceDetailList == null) this.w.dataSourceDetailList = [];

    if (!this.selected){
      this.selected = this.w.timeSeriesDetailList[0];
    }

    var i = 0;
    for (let tab of this.w.timeSeriesDetailList) {
      tab.removable = true;

      if (tab.default == null) tab.default = {};
      if (tab.primary == null) tab.primary = {};
      if (tab.success == null) tab.success = {};
      if (tab.info == null) tab.info = {};
      if (tab.warning == null) tab.default = {};
      if (tab.danger == null) tab.danger = {};
      if (tab.embeddedDashboard == null) tab.embeddedDashboard = { dashboardId: null };
      i++;
    }
    if (this.w.timeSeriesDetailList.length > 0) {
      this.w.timeSeriesDetailList[0].active = true;
    }
    this.loadTemplates();
  }

  public setSelected(series: any)
  {
    if(!series)
    {
      this.selected = null;
    }
    else
    {
      this.selected = series;
    }
  }

  public selectedPrimary(value: any): void {
    this.w.timeSeries.uri = value;
  }

  public addItem() {
    const tempItem = {
      uri: " ",
      axis: 0,
      type: "tracker",
      style: "solid",
      showMarker: false,
      size: 1,
      displayOption: "text",
      active: true,
      removable: true,
      embeddedDashoard: {},
      colour: [],
      transforms: [],
      default: {},
      primary: {},
      success: {},
      info: {},
      warning: {},
      danger: {}
    };

    this.w.timeSeriesDetailList.push(tempItem);
    this.selected = tempItem;
  }

  public removeTimeSeries(timeSeries: any): void {
    var index = this.w.timeSeriesDetailList.indexOf(timeSeries);
    if (index > -1) {
      this.w.timeSeriesDetailList.splice(index, 1);
    }

    if (this.w.timeSeriesDetailList.length > 0)
    {
      this.selected = this.w.timeSeriesDetailList[this.w.timeSeriesDetailList.length - 1];
    }
    else{
      this.selected = null;
    }
  }

  ngOnDestroy(): void {
    this.previewObjectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  private loadTemplates(): void {
    this.templatesLoading = true;
    this.templateService.WidgetTextTemplateGetAll({ isActive: true }).subscribe({
      next: (templates) => {
        this.templates = templates;
        this.templatesLoading = false;
        templates.filter(t => t.hasPreviewImage).forEach(t => {
          this.templateService.WidgetTextTemplateGetPreviewImage(t.id).subscribe({
            next: (blob) => {
              const objectUrl = URL.createObjectURL(blob);
              this.previewObjectUrls.set(t.id, objectUrl);
              this.previewImageUrls.set(t.id, this.sanitizer.bypassSecurityTrustUrl(objectUrl));
            }
          });
        });
      },
      error: () => {
        this.toastr.error('Failed to load widget templates.');
        this.templatesLoading = false;
      }
    });
  }

  public getPreviewImageUrl(id: number): SafeUrl | null {
    return this.previewImageUrls.get(id) ?? null;
  }

  public viewTemplate(template: WidgetTextTemplate): void {
    this.selectedTemplate = template;
  }

  public closeTemplateDetail(): void {
    this.selectedTemplate = null;
  }

  public applyTemplate(template: WidgetTextTemplate): void {
    if (this.w.html && !confirm(`Replace the current HTML with the "${template.name}" template?`)) {
      return;
    }
    this.w.html = template.html;
    this.selectedTemplate = null;
    if (this.editorTabs?.tabs?.length > 0) {
      this.editorTabs.tabs[0].active = true;
    }
  }

  public refreshPreview(): void {
    try {
      const template = Handlebars.compile(this.w.html || '');
      this.previewHtml = template(this.previewData);
    } catch (e: any) {
      this.previewHtml = `<div class="alert alert-danger"><strong>Template error:</strong> ${e.message}</div>`;
    }
  }

  public loadPreviewData(): void {
    const seriesWithUri = (this.w.timeSeriesDetailList || []).filter((ts: any) => ts.uri?.trim());
    if (seriesWithUri.length === 0) {
      this.toastr.warning('Please add at least one time series URI in the Data tab.');
      return;
    }

    this.isLoadingPreview = true;

    const requests: TimeSeriesDetailRequest[] = seriesWithUri.map((ts: any, index: number) => ({
      index,
      forceEmbed: false,
      period: this.w.period || null,
      timeSeries: {
        single: {
          variable: {
            uri: ts.uri,
            tagMatches: ts.tagMatches || null,
            transforms: ts.transforms?.length > 0 ? ts.transforms : null
          }
        },
        virtualTimeSeriesId: ts.virtualTimeSeriesId || null
      },
      siteUri: ts.siteUri || this.siteUri,
      siteType: this.dashboard.siteType || '',
      type: ts.sourceType || 'TimeSeries',
      timeZoneOffset: this.app.getTimeZoneOffset(this.dashboard.timeZone),
      publishedIdentifier: this.dashboard.publishedIdentifier || null
    }));

    this.dataService.DataDashboardGets({
      customerId: this.dashboard.customerId,
      dashboardId: this.dashboard.dashboardId,
      requests
    }).subscribe({
      next: (results) => {
        this.isLoadingPreview = false;
        const dataDict: any = { list: [] };

        results.forEach((result, i) => {
          if (!result?.results?.length) return;
          const series = result.results[0];
          const last: any = series.values?.length > 0 ? series.values[series.values.length - 1] : null;
          let data: any = last?.value;
          if (data && typeof data === 'string' && data.startsWith('{')) {
            try { data = JSON.parse(data); } catch { }
          }
          const item = {
            series,
            data,
            timeStamp: last?.timeStamp,
            latitude: series.latitude,
            longitude: series.longitude,
            metaData: series.metaData
          };
          if (seriesWithUri[i]?.name) {
            dataDict[seriesWithUri[i].name] = item;
          }
          dataDict.list.push(item);
        });

        if (dataDict.list.length === 0) {
          this.toastr.info('No data returned for the configured time series.');
        }

        this.previewData = dataDict;
        this.refreshPreview();
      },
      error: () => {
        this.isLoadingPreview = false;
        this.toastr.error('Failed to load preview data.');
      }
    });
  }

  public formatHtml(): void {
    if (!this.w?.html) return;
    this.w.html = this.beautifyHtml(this.w.html);
  }

  private beautifyHtml(html: string): string {
    const INDENT = '  ';
    const voidTags = new Set([
      'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
      'link', 'meta', 'param', 'source', 'track', 'wbr'
    ]);

    // Group 1: Handlebars  Group 2: HTML comments  Group 3: tags  Group 4: text/lone-{
    const tokenRegex = /(\{\{[\s\S]*?\}\})|(<!--[\s\S]*?-->)|(<[^>]*>)|([^<{]+|\{(?!\{))/g;

    interface Token { kind: 'open' | 'close' | 'void' | 'text' | 'hbs-open' | 'hbs-close' | 'hbs'; raw: string; }
    const tokens: Token[] = [];
    let m: RegExpExecArray | null;

    while ((m = tokenRegex.exec(html)) !== null) {
      const raw = m[0];
      if (m[1]) {
        if (/^\{\{#/.test(raw))       tokens.push({ kind: 'hbs-open',  raw });
        else if (/^\{\{\//.test(raw)) tokens.push({ kind: 'hbs-close', raw });
        else                           tokens.push({ kind: 'hbs',        raw });
      } else if (m[2]) {
        tokens.push({ kind: 'text', raw: raw.trim() });
      } else if (m[3]) {
        const tagName = (raw.match(/^<\/?([a-zA-Z][a-zA-Z0-9:]*)/) ?? [])[1]?.toLowerCase() ?? '';
        if (raw.startsWith('</'))                              tokens.push({ kind: 'close', raw });
        else if (raw.endsWith('/>') || voidTags.has(tagName)) tokens.push({ kind: 'void',  raw });
        else                                                   tokens.push({ kind: 'open',  raw });
      } else if (m[4]) {
        const trimmed = raw.trim();
        if (trimmed) tokens.push({ kind: 'text', raw: trimmed });
      }
    }

    let depth = 0;
    const lines: string[] = [];

    for (const tok of tokens) {
      switch (tok.kind) {
        case 'close':
        case 'hbs-close':
          depth = Math.max(0, depth - 1);
          lines.push(INDENT.repeat(depth) + tok.raw);
          break;
        case 'open':
        case 'hbs-open':
          lines.push(INDENT.repeat(depth) + tok.raw);
          depth++;
          break;
        default:
          lines.push(INDENT.repeat(depth) + tok.raw);
      }
    }

    return lines.join('\n');
  }
}
